import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';

// Icons
import { FileText, Download, Plus, Users, Calendar, Award, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportCardManagement = ({ user }) => {
  const [reportCards, setReportCards] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('list');
  
  // Generation dialog state
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [bulkGenerateDialogOpen, setBulkGenerateDialogOpen] = useState(false);
  
  const [generateForm, setGenerateForm] = useState({
    student_id: '',
    academic_year: '2024-2025',
    period: '',
    class_id: '',
    teacher_comment: '',
    principal_comment: ''
  });

  const [bulkForm, setBulkForm] = useState({
    class_id: '',
    academic_year: '2024-2025',
    period: ''
  });

  const periods = [
    { value: 'TRIMESTER_1', label: '1er Trimestre' },
    { value: 'TRIMESTER_2', label: '2ème Trimestre' },
    { value: 'TRIMESTER_3', label: '3ème Trimestre' },
    { value: 'SEMESTER_1', label: '1er Semestre' },
    { value: 'SEMESTER_2', label: '2ème Semestre' }
  ];

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'list') {
      fetchReportCards();
    }
  }, [activeTab]);

  const fetchInitialData = async () => {
    try {
      const promises = [];
      
      if (user.role !== 'STUDENT' && user.role !== 'PARENT') {
        promises.push(
          axios.get(`${API}/classes`),
          axios.get(`${API}/students`)
        );
        
        const results = await Promise.all(promises);
        setClasses(results[0].data);
        setStudents(results[1].data);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      console.error('Error fetching initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReportCards = async () => {
    try {
      const response = await axios.get(`${API}/report-cards`);
      setReportCards(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des bulletins');
      console.error('Error fetching report cards:', error);
    }
  };

  const handleGenerateReportCard = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const payload = {
        student_id: generateForm.student_id,
        academic_year: generateForm.academic_year,
        period: generateForm.period,
        class_id: generateForm.class_id || null,
        level_id: null
      };
      
      const params = new URLSearchParams();
      if (generateForm.teacher_comment) {
        params.append('teacher_comment', generateForm.teacher_comment);
      }
      if (generateForm.principal_comment) {
        params.append('principal_comment', generateForm.principal_comment);
      }
      
      const url = `${API}/report-cards/generate${params.toString() ? '?' + params.toString() : ''}`;
      const response = await axios.post(url, payload);
      
      toast.success(`Bulletin généré avec succès ! ${response.data.notifications_sent} notification(s) envoyée(s)`);
      setGenerateDialogOpen(false);
      resetGenerateForm();
      fetchReportCards();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération du bulletin');
      console.error('Error generating report card:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/report-cards/generate-bulk`, {
        class_id: bulkForm.class_id,
        academic_year: bulkForm.academic_year,
        period: bulkForm.period
      });
      
      toast.success(response.data.message);
      setBulkGenerateDialogOpen(false);
      resetBulkForm();
      fetchReportCards();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération groupée');
      console.error('Error bulk generating:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (reportId, studentName, period) => {
    try {
      const response = await axios.get(`${API}/report-cards/${reportId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bulletin_${studentName}_${period}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Téléchargement du bulletin réussi');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
      console.error('Error downloading:', error);
    }
  };

  const resetGenerateForm = () => {
    setGenerateForm({
      student_id: '',
      academic_year: '2024-2025',
      period: '',
      class_id: '',
      teacher_comment: '',
      principal_comment: ''
    });
  };

  const resetBulkForm = () => {
    setBulkForm({
      class_id: '',
      academic_year: '2024-2025',
      period: ''
    });
  };

  const getPeriodLabel = (period) => {
    const p = periods.find(item => item.value === period);
    return p ? p.label : period;
  };

  const getStatusColor = (status) => {
    const colors = {
      DRAFT: 'bg-gray-100 text-gray-800',
      GENERATED: 'bg-blue-100 text-blue-800',
      PUBLISHED: 'bg-green-100 text-green-800',
      ARCHIVED: 'bg-orange-100 text-orange-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const canGenerateReports = user.role === 'TEACHER' || user.role === 'ADMIN' || user.role === 'PRINCIPAL';

  if (loading && reportCards.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 fade-in">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <FileText className="w-6 h-6 mr-2 text-blue-600" />
            {user.role === 'STUDENT' || user.role === 'PARENT' ? 'Mes Bulletins' : 'Gestion des Bulletins'}
          </h1>
          <p className="text-gray-600 mt-1">
            {user.role === 'STUDENT' || user.role === 'PARENT'
              ? 'Consultez et téléchargez vos bulletins scolaires'
              : 'Générez et gérez les bulletins scolaires des élèves'
            }
          </p>
        </div>
        
        {canGenerateReports && (
          <div className="flex space-x-2">
            <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => setGenerateDialogOpen(true)} className="btn-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Générer Bulletin
                </Button>
              </DialogTrigger>
              
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Générer un bulletin scolaire</DialogTitle>
                  <DialogDescription>
                    Créez un bulletin pour un étudiant
                  </DialogDescription>
                </DialogHeader>
                
                <form onSubmit={handleGenerateReportCard} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="student">Étudiant *</Label>
                      <Select 
                        value={generateForm.student_id} 
                        onValueChange={(value) => setGenerateForm({ ...generateForm, student_id: value })}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionnez un étudiant" />
                        </SelectTrigger>
                        <SelectContent>
                          {students.map((student) => (
                            <SelectItem key={student.id} value={student.id}>
                              {student.first_name} {student.last_name} - {student.student_number}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="class">Classe</Label>
                      <Select 
                        value={generateForm.class_id} 
                        onValueChange={(value) => setGenerateForm({ ...generateForm, class_id: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionnez une classe" />
                        </SelectTrigger>
                        <SelectContent>
                          {classes.map((classObj) => (
                            <SelectItem key={classObj.id} value={classObj.id}>
                              {classObj.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="period">Période *</Label>
                      <Select 
                        value={generateForm.period} 
                        onValueChange={(value) => setGenerateForm({ ...generateForm, period: value })}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionnez une période" />
                        </SelectTrigger>
                        <SelectContent>
                          {periods.map((period) => (
                            <SelectItem key={period.value} value={period.value}>
                              {period.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="academic_year">Année Scolaire *</Label>
                      <Input
                        id="academic_year"
                        value={generateForm.academic_year}
                        onChange={(e) => setGenerateForm({ ...generateForm, academic_year: e.target.value })}
                        placeholder="2024-2025"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="teacher_comment">Appréciation de l'enseignant</Label>
                    <Textarea
                      id="teacher_comment"
                      value={generateForm.teacher_comment}
                      onChange={(e) => setGenerateForm({ ...generateForm, teacher_comment: e.target.value })}
                      placeholder="Commentaire général sur l'élève..."
                      rows={3}
                    />
                  </div>
                  
                  {user.role === 'PRINCIPAL' || user.role === 'ADMIN' ? (
                    <div className="space-y-2">
                      <Label htmlFor="principal_comment">Appréciation du directeur</Label>
                      <Textarea
                        id="principal_comment"
                        value={generateForm.principal_comment}
                        onChange={(e) => setGenerateForm({ ...generateForm, principal_comment: e.target.value })}
                        placeholder="Appréciation du chef d'établissement..."
                        rows={3}
                      />
                    </div>
                  ) : null}
                  
                  <div className="flex justify-end space-x-3 pt-4">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setGenerateDialogOpen(false)}
                    >
                      Annuler
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={loading}
                      className="btn-primary"
                    >
                      {loading ? 'Génération...' : 'Générer le Bulletin'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            
            {(user.role === 'ADMIN' || user.role === 'PRINCIPAL') && (
              <Dialog open={bulkGenerateDialogOpen} onOpenChange={setBulkGenerateDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={() => setBulkGenerateDialogOpen(true)} variant="outline">
                    <Users className="w-4 h-4 mr-2" />
                    Génération Groupée
                  </Button>
                </DialogTrigger>
                
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Génération groupée de bulletins</DialogTitle>
                    <DialogDescription>
                      Générez les bulletins pour toute une classe
                    </DialogDescription>
                  </DialogHeader>
                  
                  <form onSubmit={handleBulkGenerate} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="bulk_class">Classe *</Label>
                      <Select 
                        value={bulkForm.class_id} 
                        onValueChange={(value) => setBulkForm({ ...bulkForm, class_id: value })}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionnez une classe" />
                        </SelectTrigger>
                        <SelectContent>
                          {classes.map((classObj) => (
                            <SelectItem key={classObj.id} value={classObj.id}>
                              {classObj.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="bulk_period">Période *</Label>
                      <Select 
                        value={bulkForm.period} 
                        onValueChange={(value) => setBulkForm({ ...bulkForm, period: value })}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionnez une période" />
                        </SelectTrigger>
                        <SelectContent>
                          {periods.map((period) => (
                            <SelectItem key={period.value} value={period.value}>
                              {period.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="bulk_academic_year">Année Scolaire *</Label>
                      <Input
                        id="bulk_academic_year"
                        value={bulkForm.academic_year}
                        onChange={(e) => setBulkForm({ ...bulkForm, academic_year: e.target.value })}
                        placeholder="2024-2025"
                        required
                      />
                    </div>
                    
                    <div className="flex justify-end space-x-3 pt-4">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setBulkGenerateDialogOpen(false)}
                      >
                        Annuler
                      </Button>
                      <Button 
                        type="submit" 
                        disabled={loading}
                        className="btn-primary"
                      >
                        {loading ? 'Génération...' : 'Générer pour la Classe'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        )}
      </div>

      {/* Report Cards List */}
      <div className="space-y-4">
        {reportCards.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reportCards.map((report) => (
              <Card key={report.id} className="card">
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">{report.student_name}</CardTitle>
                      <CardDescription className="mt-1">
                        {report.student_number}
                      </CardDescription>
                    </div>
                    <Badge className={getStatusColor(report.status)}>
                      {report.status}
                    </Badge>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        Période:
                      </span>
                      <span className="font-medium">{getPeriodLabel(report.period)}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 flex items-center">
                        <Award className="w-4 h-4 mr-1" />
                        Année:
                      </span>
                      <span className="font-medium">{report.academic_year}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 flex items-center">
                        <TrendingUp className="w-4 h-4 mr-1" />
                        Moyenne:
                      </span>
                      <span className="font-semibold text-blue-600">
                        {report.overall_average?.toFixed(2)}/20
                      </span>
                    </div>
                    
                    {report.class_rank && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600">Rang:</span>
                        <span className="font-medium">{report.class_rank}</span>
                      </div>
                    )}
                    
                    <div className="pt-3 border-t mt-3">
                      <Button 
                        onClick={() => handleDownload(report.id, report.student_name, report.period)}
                        className="w-full btn-primary"
                        size="sm"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Télécharger PDF
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun bulletin</h3>
            <p className="text-gray-600">
              {canGenerateReports
                ? 'Commencez par générer des bulletins pour vos élèves.'
                : 'Aucun bulletin n\'est disponible pour le moment.'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportCardManagement;
